//@flow
import React, {useEffect, useRef, useState} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import {
  rgxCountries,
  getFlagByCountryCode,
} from '../../modules/on-boarding/utils';
import CustomTextField from '../CustomTextField/CustomTextField';

import type {Country} from '../../entities';
import type {InputProps, InputLabelProps} from '@material-ui/core';
import {useCountries} from '../../modules/country/utils';

type Props = {
  countries?: Country[],
  label: string,
  variant?: string,
  fullWidth?: boolean,
  name: string,
  error?: boolean,
  helperText?: string,
  inputRef?: any,
  // register: (rules?: ValidationRules) => (ref: Ref | null) => void,
  onChange?: (phone: string, value: string, prefix: string) => void,
  onError?: (error: string | null) => void,
  onInput?: (phone: string) => void,
  defaultValue?: ?string,
  defaultCountry?: Country,
  disabled?: boolean,
  className?: string,
  value?: string,

  inputProps?: InputProps,
  InputLabelProps?: InputLabelProps,
};

const useStyles = makeStyles(theme => ({
  mainWrap: {
    position: 'relative',
  },
  flagIcon: {
    width: 12,
    height: 12,
    marginRight: 6,
  },
  textField: {
    '& label': {
      paddingLeft: theme.spacing(10),
    },
    '& legend': {
      marginLeft: 59,
    },
  },
  select: {
    marginTop: '6px',
    position: 'absolute',
    left: 10,
    top: 5,
    height: 56,

    '& div': {
      backgroundColor: 'unset!important',
    },
  },
  label: {
    marginLeft: '-80px !important',
  },
  input: {
    paddingLeft: theme.spacing(12),
  },
}));

export default function PhoneInput(props: Props) {
  const {
    countries: countriesProp,
    label,
    fullWidth,
    name,
    helperText,
    onChange,
    defaultCountry,
    disabled = false,
    className,
    value: propPhone,
    InputLabelProps,
    inputProps,
    onError,
    error: propError,
    ...textFieldProps
  } = props;

  const classes = useStyles();

  const countriesStore = useCountries();

  const countries = countriesProp || countriesStore;

  const firstPrint = useRef(true);
  const [phone, setPhone] = useState('');
  const [prefix, setPrefix] = useState(
    defaultCountry ? defaultCountry.phonePrefix : '51'
  );

  const [error, setError] = useState<string | null>(null);

  const onChangePrefix = e => {
    const prefixValue = e.target.value;
    setPrefix(prefixValue);
    const fullPhone = `${prefixValue}${phone}`;
    onChange && onChange(fullPhone, phone, prefixValue);
  };

  const onChangePhone = e => {
    const phoneValue = e.target.value;
    setPhone(phoneValue);
    firstPrint.current = false;
    const fullPhone = `${prefix || ''}${phoneValue}`;
    onChange && onChange(fullPhone, phoneValue, prefix || '');
  };

  const validType = (v: string): boolean => {
    const numberRgx = /^[0-9]*$/;
    return numberRgx.test(v);
  };

  const validFormat = (v: string, prefix: string): boolean => {
    const countrySelected = countries
      ? countries.find(i => i.phonePrefix === prefix)
      : null;

    if (countrySelected && rgxCountries[countrySelected.code])
      return rgxCountries[countrySelected.code].test(v);

    return rgxCountries['DEFAULT'].test(v);
  };

  useEffect(() => {
    function validateFields(v: string, p: string) {
      if (!v) return setError('Campo requerido');
      if (!validType(v)) return setError('Formato de número inválido');
      if (!validFormat(v, p)) return setError('Formato de número inválido');
      return setError(null);
    }

    if (firstPrint.current) return;
    validateFields(phone, prefix || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, prefix]);

  useEffect(() => {
    const getPrefix = (fullPhone?: string) => {
      if (fullPhone && countries) {
        const prefix = countries.find(({phonePrefix}) =>
          phonePrefix ? fullPhone.startsWith(phonePrefix) : false
        );
        if (prefix) return prefix.phonePrefix;
        if (defaultCountry) return defaultCountry.phonePrefix;
      }

      return '51';
    };

    const getPhone = (fullPhone: string, prefix: string) => {
      if (!prefix) return fullPhone || '';

      const starWithPrefix = fullPhone.startsWith(prefix);

      if (starWithPrefix) return fullPhone.substring(prefix.length);

      return fullPhone;
    };

    if (propPhone) {
      const prefix = getPrefix(propPhone);
      const phone = getPhone(propPhone, prefix || '');
      setPrefix(prefix);
      setPhone(phone);
    }
  }, [propPhone, countries, defaultCountry]);

  useEffect(() => {
    if (onError) onError(error);
  }, [error, onError]);

  if (!countries) return null;

  return (
    <div className={`${classes.mainWrap} ${className ? className : ''}`}>
      <CustomTextField
        {...textFieldProps}
        label={<div className={classes.label}>{label}</div>}
        fullWidth={fullWidth}
        name={name}
        error={!!error || propError}
        helperText={helperText ? helperText : error ? error : ''}
        onChange={onChangePhone}
        value={phone}
        inputProps={{...inputProps, className: classes.input, value: phone}}
        className={classes.textField}
        disabled={disabled}
        InputLabelProps={{
          ...InputLabelProps,
          shrink: true,
        }}
      />
      <Select
        labelId="country"
        id="country"
        value={prefix}
        onChange={onChangePrefix}
        className={classes.select}
        disableUnderline
      >
        {!!countries &&
          countries.map(c => (
            <MenuItem key={c.phonePrefix} value={c.phonePrefix}>
              <img
                src={getFlagByCountryCode(c.code)}
                className={classes.flagIcon}
                alt={c.name}
              />
              {prefix !== c.phonePrefix && c.name} ({c.phonePrefix})
            </MenuItem>
          ))}
      </Select>
    </div>
  );
}
